import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { WEBHOOK_EMPTY_MESSAGES } from "./webhookConstants";
import "../../starek.css";

export function WebhooksEmptyState({ isFiltered }) {
    const navigate = useNavigate();
    const [message] = useState(
        () => WEBHOOK_EMPTY_MESSAGES[Math.floor(Math.random() * WEBHOOK_EMPTY_MESSAGES.length)]
    );

    return (
        <div className="d-flex flex-column align-items-center justify-content-center py-5 text-center">
            <div
                className="rounded-circle d-flex align-items-center justify-content-center mb-3"
                style={{ width: 80, height: 80, background: "rgba(0,115,183,0.08)" }}
            >
                <i className="fa fa-link fa-2x text-primary" />
            </div>
            <h5 className="text-muted mb-1">
                {isFiltered ? "No Webhooks Found" : message}
            </h5>
            <p className="text-muted small mb-3" style={{ maxWidth: 320 }}>
                {isFiltered
                    ? "No webhooks match your search. Try adjusting the filter."
                    : "No webhooks have been configured yet."}
            </p>
            {!isFiltered && (
                <button
                    className="btn btn-primary btn-sm"
                    onClick={() => navigate("/webhooks/0")}
                >
                    <i className="fa fa-plus me-1" />
                    Create Webhook
                </button>
            )}
        </div>
    );
}

function WebhooksCardView({ items, selectedRows, onSelectedRowsChange, filterText }) {
    const isSelected = (id) => selectedRows.some((row) => row.id === id);

    const handleCheckboxChange = (webhook) => {
        const next = isSelected(webhook.id)
            ? selectedRows.filter((row) => row.id !== webhook.id)
            : [...selectedRows, webhook];
        if (onSelectedRowsChange) onSelectedRowsChange({ selectedRows: next });
    };

    const filteredItems = useMemo(() => {
        const q = filterText?.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q) ||
                item.url?.toLowerCase().includes(q),
        );
    }, [items, filterText]);

    if (filteredItems.length === 0) {
        return <WebhooksEmptyState isFiltered={!!filterText?.trim()} />;
    }

    return (
        <div className="row g-3">
            {filteredItems.map((webhook) => (
                <div key={webhook.id} className="col-lg-3 col-md-4 col-sm-6">
                    <div
                        className={`card h-100 shadow-sm ${
                            isSelected(webhook.id) ? "border-primary border-2" : ""
                        }`}
                        style={{ cursor: "pointer" }}
                    >
                        <div className="card-header bg-primary text-white">
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                                    <div className="form-check mb-0 me-2 flex-shrink-0">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={isSelected(webhook.id)}
                                            onChange={() => handleCheckboxChange(webhook)}
                                            aria-label={`Select ${webhook.name}`}
                                        />
                                    </div>
                                    <h5
                                        className="card-title mb-0 text-truncate"
                                        title={webhook.name}
                                    >
                                        {webhook.name}
                                    </h5>
                                </div>
                                <i className="fa fa-link fa-lg flex-shrink-0 ms-2"></i>
                            </div>
                        </div>
                        <div className="card-body d-flex flex-column">
                            <div className="d-flex gap-1 mb-2 flex-wrap">
                                {webhook.parameters?.api_type === "GRAPHQL" ? (
                                    <span className="badge" style={{ backgroundColor: "#e10098", color: "#fff" }}>GraphQL</span>
                                ) : (
                                    <>
                                        <span className="badge bg-info text-white">REST</span>
                                        <span className="badge bg-secondary">{webhook.method || "POST"}</span>
                                    </>
                                )}
                                <span className={`badge bg-${webhook.is_active ? "success" : "secondary"}`}>
                                    {webhook.is_active ? "Active" : "Inactive"}
                                </span>
                            </div>
                            <p
                                className="text-muted mb-2"
                                style={{
                                    fontSize: "0.8rem",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                                title={webhook.url}
                            >
                                <i className="fa fa-external-link me-1"></i>
                                {webhook.url || "—"}
                            </p>
                            <p
                                className="text-muted mb-0 flex-grow-1"
                                style={{ fontSize: "0.875rem" }}
                            >
                                {webhook.description || "No description"}
                            </p>
                            <div className="border-top pt-2 mt-auto">
                                <small className="text-muted d-block">
                                    <i className="fas fa-clock me-1"></i>
                                    {moment.utc(webhook.created_at).local().format("MMM D, YYYY HH:mm")}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default WebhooksCardView;
